// Noncredential, origin-wide intents. No cookie or CSRF is persisted.
export const logoutFenceKey = 'pm-chat:pending-logout';
export const logoutFencePrefix = `${logoutFenceKey}:v2:`;
export type LogoutIntent = { id: string; handle: string };
type FenceStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;
const legacyIdPrefix = 'legacy:';
const legacyAckPrefix = `${logoutFenceKey}:legacy-ack:`;
const unavailable: LogoutIntent = { id: 'unavailable', handle: '' };
function parse(raw: string): LogoutIntent {
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== 'object' || !('id' in value) || !('handle' in value) ||
      typeof value.id !== 'string' || typeof value.handle !== 'string') throw Error('Invalid intent');
  return { id: value.id, handle: value.handle };
}
export class LogoutFence {
  private memory: LogoutIntent | null = null;
  storageFailed = false;
  constructor(private storage: FenceStorage) {}
  private keys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key?.startsWith(logoutFencePrefix)) keys.push(key);
    }
    return keys.sort();
  }
  read(): LogoutIntent | null {
    try {
      // Enumeration is not transactional. Retry detected shifts, then fail closed.
      for (let attempt = 0; attempt < 3; attempt++) {
        const legacyRaw = this.storage.getItem(logoutFenceKey);
        let legacy: LogoutIntent | null = null;
        if (legacyRaw !== null) {
          const value = parse(legacyRaw);
          if (this.storage.getItem(legacyAckPrefix + encodeURIComponent(legacyRaw)) !== legacyRaw) {
            legacy = { id: legacyIdPrefix + legacyRaw, handle: value.handle };
          }
        }
        const keys = this.keys();
        const intents: LogoutIntent[] = [];
        for (const key of keys) {
          const raw = this.storage.getItem(key);
          if (raw === null) continue;
          const intent = parse(raw);
          if (key !== logoutFencePrefix + intent.id) throw Error('Invalid intent key');
          intents.push(intent);
        }
        if (JSON.stringify(keys) !== JSON.stringify(this.keys()) || legacyRaw !== this.storage.getItem(logoutFenceKey)) continue;
        this.memory = legacy ?? intents[0] ?? (this.storageFailed ? this.memory ?? unavailable : null);
        return this.memory;
      }
      throw Error('Storage changing during read');
    } catch { this.storageFailed = true; this.memory ??= unavailable; }
    return this.memory;
  }
  begin(handle: string): LogoutIntent {
    const intent = { id: crypto.randomUUID(), handle };
    this.memory = intent;
    try { this.storage.setItem(logoutFencePrefix + intent.id, JSON.stringify(intent)); }
    catch { this.storageFailed = true; }
    return intent;
  }
  acknowledge(intent: LogoutIntent): boolean {
    try {
      if (intent.id.startsWith(legacyIdPrefix)) {
        const raw = intent.id.slice(legacyIdPrefix.length);
        if (parse(raw).handle !== intent.handle) return false;
        // Exact-payload receipt, never check/remove the shared legacy key.
        this.storage.setItem(legacyAckPrefix + encodeURIComponent(raw), raw);
        if (this.memory?.id === intent.id) this.memory = null;
        return true;
      }
      const key = logoutFencePrefix + intent.id;
      const raw = this.storage.getItem(key);
      if (raw === null) return false;
      const stored = parse(raw);
      if (stored.id !== intent.id || stored.handle !== intent.handle) return false;
      // Identity-specific immutable record: a late ACK cannot delete another begin.
      this.storage.removeItem(key);
      if (this.memory?.id === intent.id) this.memory = null;
      return true;
    } catch { this.storageFailed = true; this.memory ??= intent; return false; }
  }
}
